import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Skeleton,
  Text,
  VStack,
  useBreakpointValue
} from "@chakra-ui/react"
import { BlocksTemplateVariablesType } from "@pathwright/blocks-engine/src/types"
import { IconName } from "@pathwright/pathicons"
import { contentStyles } from "@pathwright/ui/src/components/content/typography"
import Screensize, {
  getScreensizeForWidth
} from "@pathwright/ui/src/components/ui/Screensize"
import { flattenEdges } from "@pathwright/utils"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useEffect, useMemo, useRef, useState } from "react"
import { Root, createRoot } from "react-dom/client"
import { PathItemType } from "../api/generated"
import BlockContent from "../blocks/BlockContent"
import Path from "../path/legacy/path/Path"
import {
  CreatePathFromItemsReturn,
  createPathFromItems,
  getPathStepById,
  getPathStepBySourceId,
  getStepVerbIcon
} from "../path/utils"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import CoverPageDivider from "./CoverPageDivider"
import { ImmersiveTriggerButton } from "./CoverPageImmersive"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import { CoverPagePreviewPathItem } from "./types"

type PathItems = CreatePathFromItemsReturn<CoverPagePreviewPathItem>

type PreviewPathProps = {
  data: PathItems
  isImmersive?: boolean // Path Preview in immersive mode
  maxItemHeight?: string
}

const PreviewPath = ({
  data: pathItems,
  isImmersive,
  maxItemHeight
}: PreviewPathProps) => {
  const resource = useCoverPageContext((v) => v.resource)
  const onOpenImmersive = useCoverPageContext((v) => v.onOpenImmersive)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  const handleOpenImmersive = (e: React.MouseEvent<HTMLDivElement>) => {
    if (immersiveActive) return

    onOpenImmersive?.({ name: "preview", id: "path" })
  }

  // Detect previewable steps from provided pathItems
  const previewableSteps = useMemo(() => {
    return pathItems.reduce((acc, item) => {
      if (item.items) {
        acc.push(
          ...item.items.filter(
            (i) => i.is_previewable && i.type === PathItemType.Step
          )
        )
      }
      return acc
    }, [] as CoverPagePreviewPathItem[])
  }, [pathItems])

  const previewableIds = useMemo(
    () => new Set(previewableSteps.map((s) => Number(s.source_id))),
    [previewableSteps]
  )

  // Find lesson IDs that contain at least one previewable step
  const expandedLessonIds = useMemo(() => {
    const lessonIds = new Set<number>()

    pathItems.forEach((item) => {
      if (
        item.type === PathItemType.Lesson &&
        item.items?.some(
          (i) => i.is_previewable && i.type === PathItemType.Step
        )
      ) {
        lessonIds.add(Number(item.source_id))
      }
    })

    return Array.from(lessonIds)
  }, [pathItems])

  // Build sx rules to add padding-right to previewable PathStep elements
  const previewablePaddingStyles = useMemo(() => {
    const styles: Record<string, any> = {}
    const ids = Array.from(previewableIds.values())
    ids.forEach((id) => {
      styles[`.PathStep[data-source-id="${id}"]`] = { paddingRight: "2rem" }
    })
    return styles
  }, [previewableIds])

  // Ref to the top-level wrapper to query for PathStep children
  const previewPathRef = useRef<HTMLDivElement | null>(null)

  // Keep track of mounted chevrons to clean up
  const mountedChevronsRef = useRef<
    Map<number, { container: HTMLElement; root: Root }>
  >(new Map())

  // Inject chevron icons into previewable PathStep elements
  useEffect(() => {
    const host = previewPathRef.current
    if (!host) return

    // Helper to read source id from DOM node
    const getSourceId = (el: Element): number | null => {
      const val = (el.getAttribute("data-source-id") || "").trim()
      const num = Number(val)
      return Number.isFinite(num) ? num : null
    }

    // Remove chevrons that no longer apply
    for (const [id, { container, root }] of mountedChevronsRef.current) {
      if (!previewableIds.has(id)) {
        // defer unmount to next microtask to avoid unmounting a root mid-render
        queueMicrotask(() => {
          try {
            root.unmount()
          } catch {}
          container.parentElement?.removeChild(container)
        })
        mountedChevronsRef.current.delete(id)
      }
    }

    const stepNodes = host.querySelectorAll<HTMLElement>(".PathStep")
    stepNodes.forEach((step) => {
      const id = getSourceId(step)
      if (id == null) return
      if (!previewableIds.has(id)) return
      if (mountedChevronsRef.current.has(id)) return

      // Ensure step is positioning context
      if (getComputedStyle(step).position === "static") {
        step.style.position = "relative"
      }

      const container = document.createElement("div")
      container.className = "previewpath-chevron"
      Object.assign(container.style, {
        position: "absolute",
        right: "1rem",
        top: "50%",
        pointerEvents: "none",
        zIndex: "1"
      })
      step.appendChild(container)

      // Mount Pathicon via React root
      const root = createRoot(container)

      const previewButton = isImmersive ? (
        <Button
          size="sm"
          rightIcon={
            <Pathicon
              icon={"chevron-right" as IconName}
              color="rgba(42,46,51,1)"
              size="1rem"
            />
          }
          h="32px"
          _hover={{
            backgroundColor: "rgba(42,46,51,0.01)"
          }}
          py={4}
          pl={8}
          pr={6}
          sx={{
            color: "rgba(42,46,51,1)",
            backgroundColor: "rgba(42,46,51,0.05)",
            fontWeight: "600",
            fontSize: "14px",
            padding: "8px 12px",
            backdropFilter: "blur(20px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0,
            ".chakra-button__icon": {
              marginLeft: "2px"
            }
          }}
          borderRadius="50px"
        >
          Preview
        </Button>
      ) : (
        <Pathicon icon={"chevron-right" as IconName} color="#999" size="1rem" />
      )

      root.render(previewButton)

      mountedChevronsRef.current.set(id, { container, root })
    })

    return () => {
      for (const [, { container, root }] of mountedChevronsRef.current) {
        queueMicrotask(() => {
          try {
            root.unmount()
          } catch {}
          container.parentElement?.removeChild(container)
        })
      }
      mountedChevronsRef.current.clear()
    }
  }, [previewableIds])

  return (
    <Card
      ref={previewPathRef as any}
      shadow={isImmersive ? "none" : "card"}
      borderRadius={isImmersive ? "none" : "card"}
      p={isImmersive || immersiveActive ? 0 : 4}
      bgColor={isImmersive ? "transparent" : "#F5F5F5"}
      h={isImmersive ? "100vh" : maxItemHeight}
      maxH={isImmersive ? "100%" : maxItemHeight}
      overflowY={isImmersive ? "auto" : "hidden"}
      cursor={immersiveActive ? "default" : "pointer"}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        // check if the target is a child of .path-lesson (but not .path-lesson itself);
        // if so, ignore click
        if (
          event.target instanceof HTMLElement &&
          event.target.closest(".path-lesson") &&
          event.target.closest(".path-lesson") !== event.target
        )
          return

        handleOpenImmersive(event)
      }}
      sx={{
        ".ResourceBackground": {
          position: "absolute"
        },
        ".previewpath-chevron": {
          transform: "translateY(-50%)",
          transition: "transform 120ms ease"
        },
        ".PathStep:hover .previewpath-chevron": {
          transform: "translateY(-50%) translateX(3px)"
        },
        ".chakra-card": {
          backgroundColor: "transparent"
        },
        ".path-line": {
          display: "none"
        },
        ".path-lesson": {
          margin: "0"
        },
        "header > h4": {
          fontWeight: "700",
          fontSize: "28px",
          fontFamily:
            "var(--school-heading-font), 'ProximaNova-Regular', Helvetica, sans-serif"
        },
        "header > p": {
          fontSize: "16px",
          fontWeight: "400",
          color: "#444"
        },
        ".LegacyPathView": {
          padding: immersiveActive && !isImmersive ? "20px" : "5px"
        },
        ...previewablePaddingStyles
      }}
    >
      {!isImmersive && !immersiveActive && (
        <ResourceBackground resource={resource} forceBlur />
      )}
      <Path
        items={pathItems}
        inverted
        onEnterStep={(source_id: number, rect: DOMRect) => {
          if (previewableIds.has(Number(source_id))) {
            onOpenImmersive?.({ name: "preview", id: Number(source_id) })
          }
        }}
        expandedLessonsIds={expandedLessonIds}
      />
    </Card>
  )
}

type PreviewableStepProps = {
  data: PathItems[number]
  isImmersive?: boolean
  maxItemHeight?: string
}

const useStepContainerWidth = (ref: React.RefObject<HTMLElement>) => {
  const [width, setWidth] = useState<number | undefined>()
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width
      if (typeof w === "number") setWidth(w)
    })
    if (typeof el.clientWidth === "number") setWidth(el.clientWidth)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return width
}

const mockUser = {
  id: 1,
  first_name: "John",
  last_name: "Doe",
  display_name: "John Doe",
  email: "john.doe@example.com"
}

const PreviewStep = (props: PreviewableStepProps) => {
  const { data: step, isImmersive, maxItemHeight } = props
  const { school, me } = usePathwrightContext()
  const resource = useCoverPageContext((v) => v.resource)
  const onOpenImmersive = useCoverPageContext((v) => v.onOpenImmersive)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  const handleOpenImmersive = (e: React.MouseEvent<HTMLDivElement>) => {
    if (immersiveActive) return

    onOpenImmersive?.({ name: "preview", id: step.source_id })
  }

  const [isLoading, setIsLoading] = useState(true)

  // We need to use a ref to get the width of the step container
  // so we can use it to determine the "screensize" for the content styles.
  const stepContainerRef = useRef<HTMLDivElement | null>(null)
  const stepContainerWidth = useStepContainerWidth(stepContainerRef)

  const blocksTemplateVariables = {
    step,
    school,
    user: me || mockUser
  } as BlocksTemplateVariablesType

  return (
    <Card
      className={isImmersive ? "CoverPagePreviewStep" : ""}
      shadow={isImmersive ? "none" : "card"}
      borderRadius={"card"}
      height={isImmersive ? "100%" : undefined}
      maxHeight={isImmersive ? "100%" : undefined}
      w={isImmersive ? "100%" : undefined}
      p={isImmersive || immersiveActive ? 0 : 4}
      bgColor="#F5F5F5"
      sx={{
        ".ResourceBackground": {
          position: "absolute"
        }
      }}
      h={isImmersive ? "100vh" : "100%"}
      pointerEvents={isImmersive ? "auto" : "none"}
      onClick={handleOpenImmersive}
      cursor={immersiveActive ? "default" : "pointer"}
    >
      {!isImmersive && !immersiveActive && (
        <ResourceBackground resource={resource} forceBlur />
      )}
      <Card
        bgColor="white"
        borderRadius="card"
        w="100%"
        h="100%"
        {...(isImmersive && {
          maxWidth: "1000px",
          margin: "0 auto",
          overflowY: "auto"
        })}
        overflow={isImmersive ? "auto" : "hidden"}
      >
        <CardHeader pb={2} pt={4}>
          <HStack align="center" spacing={3}>
            {step?.step_verb ? (
              <Pathicon
                icon={getStepVerbIcon(step.step_verb) as IconName}
                color="#aaa"
                size="1em"
              />
            ) : null}
            <VStack align="start" gap="1px" overflow="hidden">
              <Text
                as="h4"
                fontWeight="700"
                fontSize="md"
                color={"inherit"}
                noOfLines={2}
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                maxW="100%"
              >
                {step?.name}
              </Text>
              <HStack
                gap="5px"
                divider={
                  <Text as="span" m={0} size="micro" color="#888">
                    •
                  </Text>
                }
              >
                {!step.is_required ? (
                  <Text
                    m={0}
                    fontWeight="400"
                    as="small"
                    size="micro"
                    color="#888"
                  >
                    Optional
                  </Text>
                ) : null}
                {!!step.time_to_complete && (
                  <Text
                    m={0}
                    fontWeight="400"
                    as="small"
                    size="micro"
                    color="#888"
                  >
                    {step.time_to_complete}
                  </Text>
                )}
                {step.userpoints_value && (
                  <Text
                    m={0}
                    fontWeight="400"
                    as="small"
                    size="micro"
                    color="#888"
                  >
                    {step.userpoints_value} Points
                  </Text>
                )}
              </HStack>
            </VStack>
          </HStack>
        </CardHeader>
        <CardBody w="100%" p={0} pointerEvents={isImmersive ? "auto" : "none"}>
          {isLoading ? (
            <Skeleton
              h={maxItemHeight}
              w="100%"
              startColor="blackAlpha.50"
              endColor="blackAlpha.300"
            />
          ) : null}
          <Screensize.Provider
            screensize={getScreensizeForWidth(stepContainerWidth)}
          >
            <Box
              w="100%"
              ref={stepContainerRef}
              sx={{ ...contentStyles({ containerWidth: stepContainerWidth }) }}
            >
              <BlockContent
                accountID={step.context?.school_id!}
                contextKey={`/${step.source_id}`}
                onDataLoaded={() => {
                  setIsLoading(false)
                  requestAnimationFrame(() => {
                    window.dispatchEvent(new Event("resize"))
                  })
                }}
                templateVariables={blocksTemplateVariables}
              />
            </Box>
          </Screensize.Provider>
        </CardBody>
      </Card>
    </Card>
  )
}

export const ImmersivePreviewContainer = ({ id }: { id?: number | "path" }) => {
  const resource = useCoverPageContext((v) => v.resource)

  const path = resource?.group?.path

  if (!path) return null

  if (!id) id = "path"

  const flattenedPathItems = flattenEdges(
    path?.items || []
  ) as CoverPagePreviewPathItem[]
  const pathItems = createPathFromItems(flattenedPathItems)

  let content = null

  if (id === "path") {
    content = <PreviewPath data={pathItems} isImmersive />
  }

  const step =
    id === "path"
      ? null
      : getPathStepBySourceId(pathItems as any, Number(id)) ||
        getPathStepById(pathItems as any, Number(id))

  if (step) {
    content = <PreviewStep data={step} isImmersive />
  }

  return (
    <Box w="100%" h="100%" pos="relative" p={0} maxW="1000px" mx="auto">
      {content}
    </Box>
  )
}

export type PreviewItem =
  | {
      type: "path"
      data: PreviewPathProps["data"]
    }
  | {
      type: "step"
      data: PreviewableStepProps["data"]
    }

const useCoverPagePreviewItems = () => {
  const resource = useCoverPageContext((v) => v.resource)
  const setCanPreview = useCoverPageContext((v) => v.setCanPreview)

  const path = resource?.group?.path

  const flattenedPathItems = useMemo(
    () => flattenEdges(path?.items || []) as CoverPagePreviewPathItem[],
    [path?.items]
  )

  const pathItems = useMemo(
    () => createPathFromItems(flattenedPathItems),
    [flattenedPathItems]
  )

  const previewableSteps = useMemo(
    () =>
      pathItems.reduce((acc, item) => {
        if (item.items) {
          acc.push(
            ...item.items.filter(
              (i) => i.is_previewable && i.type === PathItemType.Step
            )
          )
        }
        return acc
      }, [] as CoverPagePreviewPathItem[]),
    [pathItems]
  )

  useEffect(() => {
    if (previewableSteps?.length || flattenedPathItems?.length) {
      setCanPreview(true)
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"))
      })
    } else {
      setCanPreview(false)
    }
  }, [previewableSteps, flattenedPathItems, setCanPreview])

  const galleryItems: PreviewItem[] = useMemo(
    () => [
      {
        type: "path",
        data: pathItems
      },
      ...previewableSteps.map((step) => ({
        type: "step" as "step",
        data: step
      }))
    ],
    [pathItems, previewableSteps]
  )

  return { galleryItems }
}

const CoverPagePathPreview = () => {
  const { galleryItems } = useCoverPagePreviewItems()

  const isMobile = useCoverPageContext((v) => v.isMobile)
  const maxItemHeight = isMobile ? "450px" : "600px"

  const previewSectionRef = useCoverPageContext((v) => v.previewSectionRef)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  const itemsPerRow = useBreakpointValue({
    base: 1,
    md: 2
  })

  const computedItemsPerRow =
    typeof itemsPerRow === "number"
      ? itemsPerRow
      : useBreakpointValue(itemsPerRow || { base: 1, sm: 1, md: 2 })

  if (!galleryItems?.length) return null

  const isPathOnly =
    galleryItems.length === 1 && galleryItems[0].type === "path"

  return (
    <>
      {!immersiveActive && <CoverPageDivider title="Preview" />}
      <HStack
        ref={previewSectionRef}
        className="CoverPagePathPreview"
        w="100%"
        scrollMarginTop="150px"
        spacing={0}
        h={isPathOnly ? "auto" : maxItemHeight}
        maxH="100%"
        sx={{
          ".carousel-item": {
            borderRadius: "card",
            maxWidth: isMobile
              ? `calc(100vw - ${COVER_PAGE_PADDING.md})`
              : "350px"
          },
          ".gallery-container, .carousel-scroller, .carousel-track, .carousel-item > .chakra-card":
            {
              height: "100%"
            }
        }}
      >
        {isPathOnly ? (
          <Box
            px={
              isMobile
                ? `calc(${COVER_PAGE_PADDING.md} / 2)`
                : COVER_PAGE_PADDING
            }
            w="100%"
          >
            <PreviewPath data={galleryItems[0].data as PathItems} />
          </Box>
        ) : (
          <CarouselGallery
            items={galleryItems}
            itemsPerRow={computedItemsPerRow}
            maxItemHeight={maxItemHeight}
            itemsFillWidth={false}
            spacingY="0"
            spacingX={
              isMobile
                ? `calc(${COVER_PAGE_PADDING.md} / 2)`
                : COVER_PAGE_PADDING
            }
            gap={20}
            enableStagger={true}
            renderItem={(item: PreviewItem) => {
              const previewProps = {
                maxItemHeight
              }
              const content =
                item.type === "path" ? (
                  <PreviewPath data={item.data} {...previewProps} />
                ) : (
                  <PreviewStep data={item.data} {...previewProps} />
                )

              return (
                <>
                  {content}
                  <Box
                    position="absolute"
                    bottom="10px"
                    left="50%"
                    transform="translateX(-50%)"
                    height="auto"
                  >
                    <ImmersiveTriggerButton
                      route={{
                        name: "preview",
                        id: item.type === "path" ? "path" : item.data.source_id
                      }}
                    />
                  </Box>
                </>
              )
            }}
            renderExpander={(item: PreviewItem) => {
              return null
            }}
          />
        )}
      </HStack>
    </>
  )
}

export default CoverPagePathPreview
